/* eslint-disable @typescript-eslint/no-unused-vars */
import { DateTime } from 'luxon';
import { Consumption, ConsumptionTableRow, isGroupedConsumption, Plant } from '@/interfaces';
import type { CellComponent, ColumnDefinition } from '@trading-ui/datagrid-v3';

import i18n from '@/i18n/config';
import { ROLES } from '@/constants/roles';

import { canEditByPricingIndexSchedule, canEditFromMaxLimitForNonAdmin } from '@/utils/dates.utils';
import { MathUtils } from '@/utils/math.utils';

import type { PricingIndex } from '@/api/services/settings/pricing-indices.service';

const BG_COLOR = 'rgba(4, 120, 178, 0.2)';
const TEXT_COLOR = '#0478B2';
const TEXT_COLOR_DIFFERENCE = '#02486B';
const DISABLED_BG_COLOR = 'rgba(200, 200, 200, 0.3)';
const DISABLED_TEXT_COLOR = '#999999';

// Custom number editor that allows arrow key navigation and only numeric input
const numberEditor = (
  cell: CellComponent,
  onRendered: (callback: () => void) => void,
  success: (value: unknown) => void,
  cancel: (value?: unknown) => void
): HTMLInputElement => {
  const cellValue = cell.getValue();
  const input = document.createElement('input');

  input.setAttribute('type', 'text');
  input.setAttribute('inputmode', 'decimal');
  input.style.width = '100%';
  input.style.height = '100%';
  input.style.boxSizing = 'border-box';
  input.style.textAlign = 'center';
  input.style.border = 'none';
  input.style.outline = 'none';
  input.style.padding = '0 4px';

  // Set initial value
  input.value = cellValue != null && cellValue !== '' ? String(cellValue) : '';

  // Only allow numeric characters, decimal point, comma, and minus
  input.addEventListener('keydown', (e) => {
    const { key } = e;

    // Allow: navigation keys, backspace, delete, tab, escape, enter
    const allowedKeys = [
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Backspace',
      'Delete',
      'Tab',
      'Escape',
      'Enter',
      'Home',
      'End',
    ];

    if (allowedKeys.includes(key)) {
      // Let ArrowLeft and ArrowRight work normally for cursor navigation
      if (key === 'ArrowLeft' || key === 'ArrowRight' || key === 'Home' || key === 'End') {
        e.stopPropagation(); // Prevent Tabulator from capturing these
        return; // Allow default behavior
      }

      if (key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        success(input.value);
        return;
      }

      if (key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        cancel();
        return;
      }

      if (key === 'Tab') {
        e.preventDefault();
        e.stopPropagation();
        success(input.value);
        return;
      }

      // Allow other navigation keys
      return;
    }

    // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
    if (e.ctrlKey || e.metaKey) {
      return;
    }

    // Allow: numbers, decimal point, comma, minus sign
    const isNumber = /^[0-9]$/.test(key);
    const isDecimal = key === '.' || key === ',';
    const isMinus = key === '-';

    if (!isNumber && !isDecimal && !isMinus) {
      e.preventDefault();
      e.stopPropagation();
    }
  });

  // Replace comma with period on input
  input.addEventListener('input', () => {
    input.value = input.value.replace(',', '.');
  });

  // Handle blur - save value
  input.addEventListener('blur', () => {
    success(input.value);
  });

  // Focus and position cursor at end after render
  onRendered(() => {
    input.focus();
    // Position cursor at the end
    const len = input.value.length;
    input.setSelectionRange(len, len);
  });

  return input;
};

/**
 * Checks if a consumption cell is editable based on pricing index time restrictions.
 * The restriction is based on the CURRENT TIME
 * Users can only edit nominations during the allowed time window.
 */
const isCellEditableByPricingIndex = (
  consumption: Consumption | null | undefined,
  pricingIndices: PricingIndex[],
  plantPricingIndexMap: Map<number, number | null>,
  userRole?: string | null
): { editable: boolean; reason?: string } => {
  if (userRole === ROLES.MOEVE_ADMIN) {
    return { editable: true };
  }

  if (!consumption?.plant) {
    return { editable: true };
  }

  const { plant } = consumption;

  const pricingIndexId = plantPricingIndexMap.get(plant.id);

  // If no pricing index assigned, cell is editable
  if (!pricingIndexId) {
    return { editable: true };
  }

  // Find the pricing index
  const pricingIndex = pricingIndices.find((pi) => pi.id === pricingIndexId);

  // If pricing index not found (shouldn't happen), allow editing
  if (!pricingIndex) {
    return { editable: true };
  }

  const isManagerOrUser = userRole === ROLES.MANAGER || userRole === ROLES.USER;

  const isEditableByPricingIndexSchedule = isManagerOrUser
    ? canEditFromMaxLimitForNonAdmin(consumption.date, pricingIndex.timeLimitLower, pricingIndex.timeLimitUpper)
    : canEditByPricingIndexSchedule(consumption.date, pricingIndex.timeLimitLower, pricingIndex.timeLimitUpper);

  if (isEditableByPricingIndexSchedule) {
    return { editable: true };
  }

  const consumptionDay = (
    typeof consumption.date === 'string' ? DateTime.fromISO(consumption.date) : DateTime.fromJSDate(consumption.date)
  ).startOf('day');
  const today = DateTime.local().startOf('day');

  if (consumptionDay <= today) {
    return {
      editable: false,
      reason: i18n.t('consumer.tablePanel.tooltip.pastDatesNotEditable', {
        index: pricingIndex.name,
      }),
    };
  }

  return {
    editable: false,
    reason: i18n.t('consumer.tablePanel.tooltip.outsidePricingIndexHours', {
      index: pricingIndex.name,
      lower: pricingIndex.timeLimitLower,
      upper: pricingIndex.timeLimitUpper,
    }),
  };
};

// Extended Plant type with company name for display
type PlantWithCompanyName = Plant & { companyName?: string };

export const getColumns = (
  pricingIndices: PricingIndex[] = [],
  plantPricingIndexMap: Map<number, number | null> = new Map(),
  duplicateIdentifiers: Set<string> = new Set(),
  duplicatePlants: PlantWithCompanyName[] = [],
  userRole: string | null = null
): ColumnDefinition[] => {
  const hasDuplicates = duplicateIdentifiers.size > 0;

  const baseColumns: ColumnDefinition[] = [
    {
      title: i18n.t('consumer.tablePanel.columns.date'),
      field: 'date',
      headerSort: false,
      minWidth: 80,
      formatter: (cell) => {
        const value = cell.getValue();
        if (!value) return '';
        const date = value instanceof Date ? value : new Date(value);
        const cellElement = cell.getElement();
        if (cellElement) {
          cellElement.style.backgroundColor = BG_COLOR;
          cellElement.style.color = TEXT_COLOR;
        }
        return date.toLocaleDateString();
      },
    },
  ];

  // When there are duplicate identifiers, generate dynamic nomination columns per company
  if (hasDuplicates && duplicatePlants.length > 0) {
    // Create one nomination column per plant
    duplicatePlants.forEach((plant) => {
      const nominationColumn: ColumnDefinition = {
        title: `${i18n.t('consumer.tablePanel.columns.nomination')}(${plant.companyName || ''})`,
        field: `nomination_${plant.id}`,
        headerSort: false,
        editor: numberEditor,
        minWidth: 100,
        editable: (cell) => {
          const row = cell.getRow();
          const rowData = row.getData() as ConsumptionTableRow;

          if (isGroupedConsumption(rowData)) {
            const consumption = rowData.consumptions.find((c) => c.plant.id === plant.id);
            if (!consumption) return false;
            const result = isCellEditableByPricingIndex(consumption, pricingIndices, plantPricingIndexMap, userRole);
            return result.editable;
          }

          return false;
        },
        formatter: (cell) => {
          const element = cell.getElement();
          const isEditing = element && element.classList.contains('tabulator-editing');

          if (isEditing) {
            return cell.getValue();
          }

          const row = cell.getRow();
          const rowData = row.getData() as ConsumptionTableRow;

          if (isGroupedConsumption(rowData)) {
            const consumption = rowData.consumptions.find((c) => c.plant.id === plant.id);

            if (!consumption) {
              if (element) {
                element.style.backgroundColor = BG_COLOR;
                element.style.color = TEXT_COLOR;
              }
              return '';
            }

            // Check if cell is disabled due to pricing index
            const editableResult = isCellEditableByPricingIndex(
              consumption,
              pricingIndices,
              plantPricingIndexMap,
              userRole
            );
            if (!editableResult.editable && element) {
              element.style.backgroundColor = DISABLED_BG_COLOR;
              element.style.color = DISABLED_TEXT_COLOR;
              element.style.cursor = 'not-allowed';
            }
            const cellValue = cell.getValue();
            const value = cellValue !== undefined ? cellValue : consumption.nomination;
            if (value == null || value === '') {
              return '';
            }

            return MathUtils.formatNumber(value);
          }

          // For non-grouped rows, these columns should be empty
          if (element) {
            element.style.backgroundColor = BG_COLOR;
            element.style.color = TEXT_COLOR;
          }
          return '';
        },
        tooltip(_event, cell) {
          const row = cell.getRow();
          const rowData = row.getData() as ConsumptionTableRow;

          if (isGroupedConsumption(rowData)) {
            const consumption = rowData.consumptions.find((c) => c.plant.id === plant.id);
            if (!consumption) return '';

            // Check if disabled due to pricing index
            const editableResult = isCellEditableByPricingIndex(
              consumption,
              pricingIndices,
              plantPricingIndexMap,
              userRole
            );
            if (!editableResult.editable && editableResult.reason) {
              return editableResult.reason;
            }

            const { updatedBy } = consumption;
            if (!updatedBy) return i18n.t('consumer.tablePanel.tooltip.pleaseInsertValueInKwh');
            return `${i18n.t('consumer.tablePanel.tooltip.updatedBy')}: ${updatedBy}`;
          }

          return '';
        },
      };

      baseColumns.push(nominationColumn);
    });

    // Add SUMATORIO column after all nomination columns
    const sumatoryColumn: ColumnDefinition = {
      title: i18n.t('consumer.tablePanel.columns.sumatory'),
      field: 'totalNomination',
      headerSort: false,
      minWidth: 100,
      formatter: (cell) => {
        const row = cell.getRow();
        const rowData = row.getData() as ConsumptionTableRow;

        const cellElement = cell.getElement();
        if (cellElement) {
          cellElement.style.backgroundColor = BG_COLOR;
          cellElement.style.color = TEXT_COLOR;
        }

        // For grouped rows, use totalNomination
        if (isGroupedConsumption(rowData)) {
          const value = rowData.totalNomination;
          if (value == null) return '';
          return MathUtils.formatNumber(value);
        }

        return '';
      },
    };
    baseColumns.push(sumatoryColumn);
  } else {
    // No duplicates: use standard nomination column
    const nominationColumn: ColumnDefinition = {
      title: i18n.t('consumer.tablePanel.columns.nomination'),
      field: 'nomination',
      headerSort: false,
      editor: numberEditor,
      minWidth: 80,
      editable: (cell) => {
        const row = cell.getRow();
        const rowData = row.getData() as ConsumptionTableRow;
        if (isGroupedConsumption(rowData)) return false;
        const result = isCellEditableByPricingIndex(rowData, pricingIndices, plantPricingIndexMap, userRole);
        return result.editable;
      },
      formatter: (cell) => {
        const element = cell.getElement();
        const isEditing = element && element.classList.contains('tabulator-editing');

        if (isEditing) {
          return cell.getValue();
        }

        const row = cell.getRow();
        const rowData = row.getData() as ConsumptionTableRow;

        if (isGroupedConsumption(rowData)) {
          return '';
        }

        const editableResult = isCellEditableByPricingIndex(rowData, pricingIndices, plantPricingIndexMap, userRole);

        if (!editableResult.editable && element) {
          element.style.backgroundColor = DISABLED_BG_COLOR;
          element.style.color = DISABLED_TEXT_COLOR;
          element.style.cursor = 'not-allowed';
        }

        const value = cell.getValue();
        if (value == null || value === '') {
          return '';
        }

        return MathUtils.formatNumber(value);
      },
      tooltip(_event, cell) {
        const row = cell.getRow();
        const rowData = row.getData() as ConsumptionTableRow;

        if (isGroupedConsumption(rowData)) return '';

        const editableResult = isCellEditableByPricingIndex(rowData, pricingIndices, plantPricingIndexMap, userRole);
        if (!editableResult.editable && editableResult.reason) {
          return editableResult.reason;
        }

        const { updatedBy } = rowData;
        if (!updatedBy) return i18n.t('consumer.tablePanel.tooltip.pleaseInsertValueInKwh');
        return `${i18n.t('consumer.tablePanel.tooltip.updatedBy')}: ${updatedBy}`;
      },
    };
    baseColumns.push(nominationColumn);
  }

  const remainingColumns: ColumnDefinition[] = [
    {
      title: i18n.t('consumer.tablePanel.columns.enagas'),
      field: 'enagas',
      headerSort: false,
      minWidth: 80,
      formatter: (cell) => {
        const value = cell.getValue();
        const cellElement = cell.getElement();
        if (cellElement) {
          cellElement.style.backgroundColor = BG_COLOR;
          cellElement.style.color = TEXT_COLOR;
        }

        const formatted = MathUtils.formatNumber(value);
        return formatted !== '' ? formatted : value;
      },
      tooltip(_event, cell) {
        const row = cell.getRow();
        const rowData = row.getData() as ConsumptionTableRow;

        // Get type from the first consumption (grouped) or directly (non-grouped)
        let type: string | null = null;
        if (isGroupedConsumption(rowData)) {
          type = rowData.consumptions[0]?.type ?? null;
        } else {
          type = (rowData as Consumption).type;
        }

        if (!type) return '';
        return type === 'C' ? 'Real' : type;
      },
    },
    {
      title: i18n.t('consumer.tablePanel.columns.difference'),
      field: hasDuplicates ? 'totalDifference' : 'difference',
      headerSort: false,
      minWidth: 80,
      formatter: (cell) => {
        const row = cell.getRow();
        const rowData = row.getData() as ConsumptionTableRow;
        const cellElement = cell.getElement();
        if (cellElement) {
          cellElement.style.backgroundColor = BG_COLOR;
          cellElement.style.color = TEXT_COLOR_DIFFERENCE;
        }

        // For grouped rows, use totalDifference
        if (isGroupedConsumption(rowData)) {
          const value = rowData.totalDifference;
          if (value == null) return '';
          return MathUtils.formatNumber(value);
        }

        // For non-grouped rows, use difference
        const value = cell.getValue();
        const formatted = MathUtils.formatNumber(value);
        return formatted !== '' ? formatted : value;
      },
    },
    {
      title: i18n.t('consumer.tablePanel.columns.cups'),
      field: hasDuplicates ? 'alias' : 'plant.alias',
      headerSort: false,
      minWidth: 250,
      formatter: (cell) => {
        const row = cell.getRow();
        const rowData = row.getData() as ConsumptionTableRow;
        const cellElement = cell.getElement();
        if (cellElement) {
          cellElement.style.backgroundColor = BG_COLOR;
          cellElement.style.color = TEXT_COLOR;
        }

        // For grouped rows, show all aliases joined with " - "
        if (isGroupedConsumption(rowData)) {
          const aliases = rowData.consumptions.map((c) => c.plant.alias);
          // Remove duplicates and join
          const uniqueAliases = [...new Set(aliases)];
          return uniqueAliases.join(' - ');
        }

        return cell.getValue();
      },
    },
  ];

  return [...baseColumns, ...remainingColumns];
};
